import React, { useState } from "react";
import { Box, Typography, Paper, Grid, Stack } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PropTypes from "prop-types";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { IconButton, Collapse, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";

// A styled component for the rotating expand icon
const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

const MyCompetencyCards = ({ competencies, t }) => {
  const getStatusConfig = (target, current) => {
    if (current === null || current === undefined) {
      return {
        label: t("user.competencies.notAssessed"),
        color: "#94a3b8", // Professional Slate Gray
        icon: <PendingActionsIcon sx={{ fontSize: 14 }} />,
        isBelow: false,
      };
    }

    if (current < target) {
      return {
        label: t("user.competencies.belowTargetText"),
        color: "#e11d48", // Professional Rose/Red (more premium than pure red)
        icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
        isBelow: true,
      };
    }

    if (current === target) {
      return {
        label: t("user.competencies.onTargetText"),
        color: "#2563eb", // Deep "SaaS Blue"
        icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
        isBelow: false,
      };
    }

    return {
      label: t("user.competencies.aboveTargetText"),
      color: "#059669", // Emerald Green (Premium corporate look)
      icon: <VerifiedIcon sx={{ fontSize: 16 }} />,
      isBelow: false,
    };
  };

  const [expandedId, setExpandedId] = useState(null);

  return (
    <Box mt={2}>
      <Typography variant="h6" fontWeight="700" sx={{ mb: 2 }}>
        {t("user.competencies.detailsTitle")}
      </Typography>

      <Stack spacing={2}>
        {competencies.map((c, index) => {
          const status = getStatusConfig(c.targetLevelValue, c.myLevelValue);

          return (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: "12px",
                borderLeft: `6px solid ${status.color}`,
                backgroundColor: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
                borderTop: "1px solid #eee",
                borderRight: "1px solid #eee",
                borderBottom: "1px solid #eee",
              }}
            >
              <Box>
                <Grid container alignItems="center" spacing={2}>
                  {/* 1. Name */}
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1" fontWeight="700">
                      {c.competencyName}
                    </Typography>
                  </Grid>

                  {/* 2. Target Level */}
                  <Grid item xs={4} md={3}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {t("user.competencies.target")}
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {c.targetLevelName
                        ? `${c.targetLevelName}${c.targetLevelValue ? ` (${c.targetLevelValue})` : ""}`
                        : "—"}
                    </Typography>
                  </Grid>

                  {/* 3. My Level */}
                  <Grid item xs={4} md={3}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {t("user.competencies.myLevel")}
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {c.myLevelName
                        ? `${c.myLevelName}${c.myLevelValue ? ` (${c.myLevelValue})` : ""}`
                        : "—"}
                    </Typography>
                  </Grid>

                  {/* 4. Status */}
                  <Grid item xs={4} md={3}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      {t("user.competencies.status")}
                    </Typography>
                    <Stack
                      direction={{ xs: "column", md: "row" }} // Column on mobile, Row on desktop
                      alignItems={{ xs: "flex-start", md: "center" }} // Align left on mobile, center on desktop
                      spacing={{ xs: 0.5, md: 1 }} // Tighter spacing on mobile
                      sx={{
                        color: status.color,
                        // height: "1.5rem"
                        height: { xs: "auto", md: "1.5rem" },
                      }}
                    >
                      <Typography variant="body2" fontWeight="800">
                        {status.label}
                      </Typography>
                      {status.icon}
                      {/* --- NEW: Expansion Toggle Button Column --- */}
                      {status.isBelow && ( // Only show if below target
                        <ExpandMore
                          expand={expandedId === c.id}
                          onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                        >
                          <ExpandMoreIcon />
                        </ExpandMore>
                      )}
                      {/* --- END NEW --- */}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
              {/* --- NEW: --- */}
              <Collapse in={expandedId === c.id} timeout="auto" unmountOnExit>
                <Divider />
                <Box sx={{ p: 1, backgroundColor: "#fafafa" }}>
                  <Typography variant="subtitle2" color="error" fontWeight="700" sx={{ mb: 1 }}>
                    {t("user.competencies.kpiAffect")}:
                  </Typography>
                  {c.kpiLinks && c.kpiLinks.length > 0 ? (
                    <Grid container spacing={2}>
                      {c.kpiLinks.map((kpi, kIdx) => (
                        <Grid item xs={12} sm={6} md={4} key={kIdx}>
                          <Paper
                            variant="outlined"
                            sx={{ p: 1.5, borderRadius: "8px", borderLeft: "4px solid #e11d48" }}
                          >
                            <Typography variant="body2" fontWeight="700">
                              {kpi.kpiName}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                              {t("user.competencies.weight")}: <b>{kpi.weightage}%</b>
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {t("user.competencies.noData")}
                    </Typography>
                  )}
                </Box>
              </Collapse>
              {/* --- NEW: --- */}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

// 2. Define the prop types
MyCompetencyCards.propTypes = {
  // t should be a translation function (required)
  t: PropTypes.func.isRequired,

  // competencies should be an array of objects with a specific shape
  competencies: PropTypes.arrayOf(
    PropTypes.shape({
      competencyName: PropTypes.string.isRequired,
      targetLevelName: PropTypes.string.isRequired,
      targetLevelValue: PropTypes.number.isRequired,
      myLevelName: PropTypes.string, // Optional as it can be null
      myLevelValue: PropTypes.number, // Optional as it can be null
    })
  ).isRequired,
};

// 3. Define default props (optional but good practice)
MyCompetencyCards.defaultProps = {
  competencies: [],
};

export default MyCompetencyCards;
